/**
 * Swift generator suite - Composed generator with dependency injection
 * Provides a unified functional interface for all Swift code generation
 *
 * This suite provides a fully functional architecture with dependency injection:
 * - Type mappers: Functional (DI ready)
 * - Code emitters: Functional (DI ready)
 * - Protocol generators: Functional (DI ready)
 * - Struct generators: Functional (DI ready)
 * - Method generators: Functional (DI ready)
 * - Bridge generators: Functional (DI ready)
 *
 * This approach provides:
 * ✅ Clean functional API for users
 * ✅ Full dependency injection throughout the stack
 * ✅ Easy testing and customization
 * ✅ Composable and predictable code generation
 */

import type { ParsedModule } from "../../parser";
import type {
  SwiftCodeEmitter as SwiftCodeEmitterInterface,
  SwiftCodeEmitterOptions,
} from "./swift-code-emitter";
import type {
  SwiftTypeMapper as SwiftTypeMapperInterface,
  SwiftTypeMappingOptions,
} from "./swift-type-mapper";
import { createSwiftCodeEmitter } from "./swift-code-emitter";
import { createSwiftTypeMapper } from "./swift-type-mapper";

// Import functional generators
import type { SwiftProtocolGenerator as SwiftProtocolGeneratorInterface } from "./swift-protocol-generator";
import type { SwiftStructGenerator as SwiftStructGeneratorInterface } from "./swift-struct-generator";
import type { SwiftMethodGenerator as SwiftMethodGeneratorInterface } from "./swift-method-generator";
import type { SwiftBridgeGenerator as SwiftBridgeGeneratorInterface } from "./swift-bridge-generator";
import { createSwiftProtocolGenerator } from "./swift-protocol-generator";
import { createSwiftStructGenerator } from "./swift-struct-generator";
import { createSwiftMethodGenerator } from "./swift-method-generator";
import { createSwiftBridgeGenerator } from "./swift-bridge-generator";

export interface SwiftGeneratorSuiteOptions {
  /**
   * Type mapping options
   */
  typeMappingOptions?: SwiftTypeMappingOptions;
  /**
   * Code emitter options
   */
  codeEmitterOptions?: SwiftCodeEmitterOptions;
}

export interface SwiftGeneratorSuiteDeps {
  typeMapper: SwiftTypeMapperInterface;
  codeEmitter: SwiftCodeEmitterInterface;
  protocolGenerator: SwiftProtocolGeneratorInterface;
  structGenerator: SwiftStructGeneratorInterface;
  methodGenerator: SwiftMethodGeneratorInterface;
  bridgeGenerator: SwiftBridgeGeneratorInterface;
}

export interface SwiftGeneratorSuite {
  /**
   * Dependencies used by this suite
   */
  deps: SwiftGeneratorSuiteDeps;

  /**
   * Generate protocol/interface for a module
   */
  generateProtocol(module: ParsedModule): string;

  /**
   * Generate struct types for a module
   */
  generateStructs(module: ParsedModule): string;

  /**
   * Generate bridge implementation files
   */
  generateBridge(modules: ParsedModule[]): {
    bridgeHeader: string;
    bridgeImplementation: string;
    swiftImplementation: string;
  };

  /**
   * Generate complete Swift implementation for a module
   */
  generateSwiftImplementation(module: ParsedModule): string;

  /**
   * Generate library mode protocol (public API)
   */
  generateLibraryProtocol(module: ParsedModule): string;

  /**
   * Generate library mode structs (Codable types)
   */
  generateLibraryStructs(module: ParsedModule): string;
}

/**
 * Creates a Swift generator suite with composed dependencies
 */
export function createSwiftGeneratorSuite(
  options: SwiftGeneratorSuiteOptions = {},
): SwiftGeneratorSuite {
  const { typeMappingOptions = {}, codeEmitterOptions = {} } = options;

  // Create base dependencies
  const typeMapper = createSwiftTypeMapper(typeMappingOptions);
  const codeEmitter = createSwiftCodeEmitter(codeEmitterOptions);

  // Create functional generators with dependencies
  const protocolGenerator = createSwiftProtocolGenerator({ typeMapper, codeEmitter });
  const structGenerator = createSwiftStructGenerator({ typeMapper, codeEmitter });
  const methodGenerator = createSwiftMethodGenerator({ typeMapper });
  const bridgeGenerator = createSwiftBridgeGenerator({ typeMapper, codeEmitter });

  const deps: SwiftGeneratorSuiteDeps = {
    typeMapper,
    codeEmitter,
    protocolGenerator,
    structGenerator,
    methodGenerator,
    bridgeGenerator,
  };

  /**
   * Generate protocol/interface for a module
   */
  function generateProtocol(module: ParsedModule): string {
    return protocolGenerator.generateModuleProtocol(module);
  }

  /**
   * Generate struct types for a module
   */
  function generateStructs(module: ParsedModule): string {
    return structGenerator.generateModuleTypes(module);
  }

  /**
   * Generate bridge implementation files
   */
  function generateBridge(_modules: ParsedModule[]): {
    bridgeHeader: string;
    bridgeImplementation: string;
    swiftImplementation: string;
  } {
    const bridgeHeader = bridgeGenerator.generateBrickCodegenHeader();

    return {
      bridgeHeader,
      bridgeImplementation:
        "// Use IOSPlatform.generateFiles() for full bridge generation",
      swiftImplementation:
        "// Use IOSPlatform.generateFiles() for full bridge generation",
    };
  }

  /**
   * Generate complete Swift implementation for a module
   */
  function generateSwiftImplementation(_module: ParsedModule): string {
    return "// Use IOSPlatform.generateFiles() for full implementation";
  }

  /**
   * Generate library mode protocol (public API)
   */
  function generateLibraryProtocol(module: ParsedModule): string {
    return protocolGenerator.generateModuleProtocol(module);
  }

  /**
   * Generate library mode structs (Codable types)
   */
  function generateLibraryStructs(module: ParsedModule): string {
    return structGenerator.generateModuleTypes(module);
  }

  return {
    deps,
    generateProtocol,
    generateStructs,
    generateBridge,
    generateSwiftImplementation,
    generateLibraryProtocol,
    generateLibraryStructs,
  };
}

/**
 * Helper to create a Swift generator suite with module-specific options
 */
export function createSwiftGeneratorForModule(
  module: ParsedModule,
): SwiftGeneratorSuite {
  return createSwiftGeneratorSuite({
    typeMappingOptions: {
      moduleName: module.moduleName,
      interfaces: module.interfaceDefinitions,
    },
  });
}
